import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ShieldCheck, X } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampPuces } from "@/composants/ui/champ";
import { Vide } from "@/composants/ui/etats";
import { Donnee, Etiquette } from "@/composants/ui/etiquette";
import {
  dateSemaine,
  euros,
  horodatage,
  natureFrais,
  pourcentage,
  quantiteLisible,
} from "@/lib/libelles";
import type {
  EcartPrix,
  FicheCommandeFournisseur,
  GesteEcartPrix,
  NaturePrix,
  StatutEcartPrix,
} from "@/lib/api";
import { api } from "@/lib/api";

type Agir = (geste: () => Promise<FicheCommandeFournisseur>) => Promise<boolean>;

const STATUTS: Record<StatutEcartPrix, string> = {
  A_VALIDER: "À valider",
  AUTORISE: "Autorisé, à confirmer",
  ACCEPTE: "Accepté",
  CONTESTE: "Contesté",
  RESORBE: "Résorbé",
  DETACHE: "Tombé : AR détaché",
};

/** Ticket #72 (décision 105) : ce que l'acceptation d'un écart de ligne dit du prix. */
export const NATURES_DU_PRIX: Record<NaturePrix, string> = {
  NOUVEAU_TARIF: "Nouveau tarif",
  PRIX_DE_CETTE_COMMANDE: "Prix de cette commande seule",
};

/** La couleur souligne le mot, elle ne le remplace pas (ADR-0024). */
const TONS: Record<StatutEcartPrix, "alerte" | "succes" | "neutre"> = {
  A_VALIDER: "alerte",
  AUTORISE: "alerte",
  ACCEPTE: "succes",
  CONTESTE: "neutre",
  RESORBE: "neutre",
  DETACHE: "neutre",
};

/** Hausse ou baisse, en toutes lettres, contre le prix initial. */
export function sensDeLEcart(ecart: EcartPrix) {
  if (ecart.pourcentage === null) {
    return ecart.prix_initial_ht === null
      ? "la ligne est partie sans prix"
      : "sur un prix initial nul";
  }
  if (ecart.pourcentage > 0) return `hausse de ${pourcentage(ecart.pourcentage)}`;
  return `baisse de ${pourcentage(-ecart.pourcentage)}`;
}

/**
 * Les écarts de prix d'une commande fournisseur (LOT 2, ticket #39, ADR-0045
 * amendée) : la base les crée quand l'AR courant donne à une ligne, ou à des
 * frais, un autre prix que le prix retenu. L'écran lit ce que le serveur dit —
 * `geste`, `attente` — et ne recopie ni le plafond, ni le périmètre, ni qui
 * confirme (ticket #46).
 * Sans la permission de voir les montants, la fiche n'en porte aucun.
 */
export function EcartsPrixDeLaCommande({
  fiche,
  agir,
}: {
  fiche: FicheCommandeFournisseur;
  agir: Agir;
}) {
  if (fiche.ecarts_prix === null) return null;
  const enAttente = fiche.ecarts_prix.filter(
    (ecart) => ecart.statut === "A_VALIDER" || ecart.statut === "AUTORISE",
  ).length;
  return (
    <Carte>
      <EnTeteCarte className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-encre">Écarts de prix fournisseur</h2>
        {enAttente > 0 ? (
          <Etiquette ton="alerte">
            {enAttente === 1 ? "1 écart en attente" : `${enAttente} écarts en attente`}
          </Etiquette>
        ) : null}
      </EnTeteCarte>
      {fiche.ecarts_prix.length === 0 ? (
        <Vide
          titre="Aucun écart de prix"
          detail="Les prix des AR sont ceux de la commande, frais compris."
        />
      ) : (
        <ul className="divide-y divide-filet">
          {fiche.ecarts_prix.map((ecart) => (
            <li key={ecart.id} className="px-4 py-3">
              <EcartPrixDetail
                ecart={ecart}
                decider={(motif, nature_prix) =>
                  agir(() => api.deciderEcartPrix(fiche.id, ecart.id, { motif, nature_prix }))
                }
                contester={(motif) =>
                  agir(() => api.contesterEcartPrix(fiche.id, ecart.id, { motif }))
                }
                autoriser={() => agir(() => api.autoriserEcartPrix(fiche.id, ecart.id))}
              />
            </li>
          ))}
        </ul>
      )}
    </Carte>
  );
}

/** Ce que chaque geste dit sur ses boutons (ticket #46). */
type Libelles = { verbe: string; bouton: string; motif: string };

const LIBELLES_DU_GESTE: Record<GesteEcartPrix, Libelles> = {
  DECIDER: {
    verbe: "Accepter",
    bouton: "Accepter le nouveau prix",
    motif: "Motif de la décision",
  },
  AUTORISER: {
    verbe: "Autoriser",
    bouton: "Autoriser le nouveau prix",
    motif: "Motif, pour contester",
  },
  CONFIRMER: {
    verbe: "Confirmer",
    bouton: "Confirmer le nouveau prix",
    motif: "Motif de la décision",
  },
};

/**
 * Un écart, avec ce qu'il faut pour le décider : le prix initial, le nouveau,
 * l'écart en % et en euros, et la marge prévisionnelle sur achats avant et
 * après ; décidé, qui, quand, pourquoi — et, passé par l'autorisation, qui l'a
 * autorisé. `avecCommande` le situe, dans la liste des écarts à valider.
 */
export function EcartPrixDetail({
  ecart,
  avecCommande = false,
  decider,
  contester,
  autoriser,
}: {
  ecart: EcartPrix;
  avecCommande?: boolean;
  decider: (motif: string, nature: NaturePrix | undefined) => Promise<boolean>;
  contester: (motif: string) => Promise<boolean>;
  autoriser: () => Promise<boolean>;
}) {
  const [motif, setMotif] = useState("");
  // Ticket #72 : aucune puce n'est cochée d'avance, le choix est celui du chargé d'affaires.
  const [nature, setNature] = useState<NaturePrix | "">("");
  const [envoi, setEnvoi] = useState(false);
  const surLesFrais = ecart.frais_commande_fournisseur_id !== null;
  const trancher = async (geste: (motif: string) => Promise<boolean>) => {
    setEnvoi(true);
    if (await geste(motif.trim())) setMotif("");
    setEnvoi(false);
  };
  const accepter = async () => {
    setEnvoi(true);
    if (await decider(motif.trim(), nature === "" ? undefined : nature)) {
      setMotif("");
      setNature("");
    }
    setEnvoi(false);
  };
  const donnerSonAccord = async () => {
    setEnvoi(true);
    if (await autoriser()) setMotif("");
    setEnvoi(false);
  };
  const geste = ecart.geste;
  // Accepter un écart de ligne dit la nature du prix ; autoriser ou contester, non ; des frais, non.
  const natureAttendue = !surLesFrais && (geste === "DECIDER" || geste === "CONFIRMER");

  return (
    <article
      aria-label={`Écart de prix : ${ecart.objet}`}
      className="flex flex-col gap-3 text-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-encre">
            {surLesFrais ? `Frais « ${ecart.objet} »` : ecart.objet} : {sensDeLEcart(ecart)}
          </p>
          <p className="text-encre-douce">
            {ecart.nature_frais ? `Nature : ${natureFrais(ecart.nature_frais)} · ` : null}
            {avecCommande ? (
              <>
                <Link
                  to={`/commandes-fournisseurs/${ecart.commande_fournisseur_id}`}
                  className="text-encre underline underline-offset-4"
                >
                  Commande chez {ecart.fournisseur}
                </Link>
                {ecart.numero_commande_client ? (
                  <>
                    {" "}
                    pour <span className="font-mono">{ecart.numero_commande_client}</span>
                  </>
                ) : null}
                {" · "}
              </>
            ) : null}
            {ecart.date_ar === null
              ? "Frais ajoutés après l'envoi"
              : `AR du ${dateSemaine(ecart.date_ar)}`}
          </p>
        </div>
        <Etiquette ton={TONS[ecart.statut]}>
          {ecart.tolerance_appliquee !== null ? "Accepté d'office" : STATUTS[ecart.statut]}
        </Etiquette>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        <Donnee libelle="Prix initial">
          <span className="tabular-nums">
            {ecart.prix_initial_ht === null ? "Sans prix" : euros(ecart.prix_initial_ht)}
          </span>
        </Donnee>
        {ecart.prix_precedent_ht !== ecart.prix_initial_ht ? (
          <Donnee libelle="Prix retenu jusqu'ici">
            <span className="tabular-nums">{euros(ecart.prix_precedent_ht)}</span>
          </Donnee>
        ) : null}
        <Donnee libelle={ecart.date_ar === null ? "Montant demandé" : "Nouveau prix, sur l'AR"}>
          <span className="tabular-nums">{euros(ecart.prix_ar_ht)}</span>
        </Donnee>
        <Donnee libelle="Écart sur le prix initial">
          {ecart.pourcentage === null ? "Non mesurable" : sensDeLEcart(ecart)}
        </Donnee>
        <Donnee libelle="Incidence si accepté">
          <span className="tabular-nums">
            {euros(ecart.incidence_ht)}
            {ecart.quantite !== null ? ` (sur ${quantiteLisible(ecart.quantite)})` : null}
          </span>
        </Donnee>
      </dl>

      {ecart.marges.length > 0 ? (
        <div className="flex flex-col gap-1">
          <p className="text-xs text-encre-douce">Marge prévisionnelle sur achats</p>
          <ul className="flex flex-col gap-1">
            {ecart.marges.map((marge) => (
              <li key={marge.commande_client_id} className="tabular-nums text-encre">
                <span className="font-mono">{marge.numero_commande_client}</span> : avant{" "}
                {marge.avant_ht === null ? "inconnue (sans montant de vente)" : euros(marge.avant_ht)},
                après {marge.apres_ht === null ? "inconnue" : euros(marge.apres_ht)}
                {marge.incomplete ? " (marge incomplète : le coût ne compte pas tout)" : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Issue ecart={ecart} />

      {geste || ecart.contestable ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(evenement) => evenement.preventDefault()}
        >
          {geste === "AUTORISER" ? (
            <p className="text-encre">
              {`Cette hausse dépasse ce que ${ecart.charge_affaires ?? "le chargé d'affaires"},`}
              {" chargé d'affaires concerné, décide seul :"}
              {" autorisée, elle attendra sa confirmation."}
            </p>
          ) : null}
          {geste === "CONFIRMER" ? (
            <p className="text-encre">La confirmation vous revient.</p>
          ) : null}
          {natureAttendue ? (
            <ChampPuces
              libelle="Nature du prix"
              required
              options={Object.entries(NATURES_DU_PRIX).map(([valeur, libelle]) => ({
                valeur,
                libelle,
              }))}
              valeur={nature}
              onChanger={(valeur) => setNature(valeur as NaturePrix)}
            />
          ) : null}
          <Champ
            libelle={geste ? LIBELLES_DU_GESTE[geste].motif : "Motif, pour contester"}
            required={geste !== "AUTORISER"}
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            {geste ? (
              <Bouton
                type="button"
                disabled={
                  envoi || (geste !== "AUTORISER" && !motif.trim()) || (natureAttendue && !nature)
                }
                aria-label={`${LIBELLES_DU_GESTE[geste].verbe} l'écart : ${ecart.objet}`}
                onClick={() => (geste === "AUTORISER" ? donnerSonAccord() : accepter())}
              >
                {geste === "AUTORISER" ? (
                  <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <Check className="h-3.5 w-3.5" aria-hidden />
                )}
                {LIBELLES_DU_GESTE[geste].bouton}
              </Bouton>
            ) : null}
            <Bouton
              type="button"
              variante="discret"
              disabled={envoi || !motif.trim()}
              aria-label={`Contester l'écart : ${ecart.objet}`}
              onClick={() => trancher(contester)}
            >
              <X className="h-3.5 w-3.5" aria-hidden />
              Contester, et ouvrir un problème
            </Bouton>
          </div>
        </form>
      ) : null}
    </article>
  );
}

/** Ce que l'écart est devenu, ou pourquoi il attend, en toutes lettres. */
function Issue({ ecart }: { ecart: EcartPrix }) {
  const plafond =
    ecart.plafond_applique === null
      ? "sans plafond"
      : `plafond de ${pourcentage(ecart.plafond_applique)}`;
  // Ticket #46 : la première étape d'une double validation se garde.
  const autorisation = ecart.autorise_par
    ? `Autorisé par ${ecart.autorise_par} le ${horodatage(ecart.autorise_le)} (${plafond})`
    : null;
  switch (ecart.statut) {
    case "A_VALIDER":
      return ecart.attente ? <p className="text-encre">{ecart.attente}</p> : null;
    case "AUTORISE":
      return (
        <p className="text-encre">
          {`${autorisation}.`}
          {ecart.attente ? ` ${ecart.attente}` : null}
        </p>
      );
    case "ACCEPTE":
      // Ticket #50 : dans la tolérance sur les frais, la base l'accepte seule.
      if (ecart.tolerance_appliquee !== null) {
        return (
          <p className="text-encre">
            {`Accepté d'office le ${horodatage(ecart.decide_le)},`}
            {" dans la tolérance sur les frais"}
            {` de ${pourcentage(ecart.tolerance_appliquee)} : personne n'avait à le valider.`}
            {" Le nouveau montant est le montant retenu."}
          </p>
        );
      }
      return (
        <p className="text-encre">
          {autorisation ? `${autorisation}, confirmé` : `Accepté`}
          {` par ${ecart.decideur} le ${horodatage(ecart.decide_le)}`}
          {autorisation ? " :" : ` (${plafond}) :`}{" "}
          {`« ${ecart.motif} ». Le nouveau prix est le prix retenu.`}
          {ecart.nature_prix ? ` Nature du prix : ${NATURES_DU_PRIX[ecart.nature_prix]}.` : null}
        </p>
      );
    case "CONTESTE":
      return (
        <p className="text-encre">
          {autorisation ? `${autorisation}, puis contesté` : "Contesté"}
          {` par ${ecart.decideur} le ${horodatage(ecart.decide_le)} :`}{" "}
          {`« ${ecart.motif} ». Un problème fournisseur est ouvert.`}
        </p>
      );
    case "RESORBE":
      return (
        <p className="text-encre-douce">
          Résorbé par l'AR du {dateSemaine(ecart.resorbe_par_date_ar)}.
        </p>
      );
    case "DETACHE":
      return (
        <p className="text-encre-douce">
          L'AR du {dateSemaine(ecart.date_ar)} a été détaché de cette commande : l'écart ne se décide
          plus.
        </p>
      );
  }
}
